import React, { useState, useEffect } from 'react'
import { Settings, Check, X, Clock, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react'
import { adminApi } from '../services/api'
import toast from 'react-hot-toast'

const TenantFeatureToggle = ({ tenant, isOpen, onClose }) => {
  const [features, setFeatures] = useState([])
  const [tenantFeatures, setTenantFeatures] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [toggleLoading, setToggleLoading] = useState({})
  const [expandedCategories, setExpandedCategories] = useState({})

  useEffect(() => {
    if (isOpen && tenant) {
      fetchData()
    }
  }, [isOpen, tenant])

  const fetchData = async () => {
    try {
      setIsLoading(true)
      const [featuresResponse, tenantFeaturesResponse] = await Promise.all([
        adminApi.getFeatures(),
        adminApi.getTenant(tenant.id)
      ])
      
      setFeatures(featuresResponse.data.features || [])
      setTenantFeatures(tenantFeaturesResponse.data.tenant?.tenantFeatures || [])
    } catch (error) {
      console.error('Error fetching data:', error)
      toast.error('Failed to load features')
    } finally {
      setIsLoading(false)
    }
  }

  const handleToggleFeature = async (featureId, currentEnabled) => {
    try {
      setToggleLoading(prev => ({ ...prev, [featureId]: true }))
      
      console.log('Toggling feature:', { 
        tenantId: tenant.id, 
        featureId, 
        currentEnabled, 
        newEnabled: !currentEnabled 
      })
      
      const response = await adminApi.toggleTenantFeature(tenant.id, featureId, !currentEnabled)
      
      console.log('Toggle response:', response)
      
      toast.success(`Feature ${!currentEnabled ? 'enabled' : 'disabled'} successfully`)
      
      // Refresh data
      await fetchData()
    } catch (error) {
      console.error('Error toggling feature:', error)
      console.error('Error details:', error.response?.data)
      
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error'
      toast.error(`Failed to toggle feature: ${errorMessage}`)
    } finally {
      setToggleLoading(prev => ({ ...prev, [featureId]: false }))
    }
  }

  const isFeatureEnabled = (featureId) => {
    return tenantFeatures.some(tf => tf.featureId === featureId && tf.isEnabled)
  }

  const getFeatureStatus = (feature) => {
    const tenantFeature = tenantFeatures.find(tf => tf.featureId === feature.id)
    
    if (!tenantFeature) return 'not_assigned'
    if (tenantFeature.isEnabled) return 'enabled'
    return 'disabled'
  }

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }))
  }

  const groupedFeatures = features.reduce((acc, feature) => {
    if (!acc[feature.category]) {
      acc[feature.category] = []
    }
    acc[feature.category].push(feature)
    return acc
  }, {})

  const getStatusColor = (status) => {
    switch (status) {
      case 'enabled':
        return 'bg-green-100 text-green-800'
      case 'disabled':
        return 'bg-red-100 text-red-800'
      case 'not_assigned':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'enabled':
        return <Check size={16} className="text-green-600" />
      case 'disabled':
        return <X size={16} className="text-red-600" />
      case 'not_assigned':
        return <Clock size={16} className="text-gray-600" />
      default:
        return <AlertCircle size={16} className="text-gray-600" />
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Feature Management
              </h3>
              <p className="text-sm text-gray-600">
                {tenant.name} ({tenant.subdomain})
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {isLoading ? (
            <div className="animate-pulse space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedFeatures).map(([category, categoryFeatures]) => (
                <div key={category} className="border rounded-lg">
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {expandedCategories[category] ? (
                        <ChevronDown size={20} />
                      ) : (
                        <ChevronRight size={20} />
                      )}
                      <span className="font-medium text-gray-900 capitalize">
                        {category}
                      </span>
                      <span className="text-sm text-gray-500">
                        ({categoryFeatures.length} features)
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {categoryFeatures.map(feature => {
                        const status = getFeatureStatus(feature)
                        return (
                          <span key={feature.id} className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs ${getStatusColor(status)}`}>
                            {getStatusIcon(status)}
                          </span>
                        )
                      })}
                    </div>
                  </button>
                  
                  {expandedCategories[category] && (
                    <div className="p-4 space-y-3">
                      {categoryFeatures.map(feature => {
                        const status = getFeatureStatus(feature)
                        const isEnabled = status === 'enabled'
                        const isLoading = toggleLoading[feature.id]
                        
                        return (
                          <div key={feature.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                                  {getStatusIcon(status)}
                                  <span className="ml-1 capitalize">{status.replace('_', ' ')}</span>
                                </span>
                                <div>
                                  <h4 className="font-medium text-gray-900">{feature.name}</h4>
                                  <p className="text-sm text-gray-600">{feature.description}</p>
                                </div>
                              </div>
                              <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                                <span>Code: {feature.code}</span>
                                <span>Plan: {feature.requiredPlan}</span>
                                {feature.isCore && <span className="text-blue-600">Core Feature</span>}
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleToggleFeature(feature.id, isEnabled)}
                                disabled={isLoading || feature.isCore}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                  isEnabled 
                                    ? 'bg-blue-600' 
                                    : 'bg-gray-200'
                                } ${
                                  feature.isCore 
                                    ? 'opacity-50 cursor-not-allowed' 
                                    : 'cursor-pointer'
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    isEnabled ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                              
                              {isLoading && (
                                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                              )}
                              
                              {feature.isCore && (
                                <span className="text-xs text-gray-500">Core</span>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              ))}
              
              {Object.keys(groupedFeatures).length === 0 && (
                <div className="text-center py-8">
                  <Settings className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-500">No features found</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-6 border-t bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-green-100 rounded-full"></div>
                <span>Enabled</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-red-100 rounded-full"></div>
                <span>Disabled</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-gray-100 rounded-full"></div>
                <span>Not Assigned</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="btn btn-secondary"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TenantFeatureToggle 